import { EngagementDto, UserRolesEnum } from '@families-link/shared';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellAction, CoreTableActionsCell } from '../../../_core/components/_ui/core-table';
import { EyeIcon, ShareIcon, SendSmsIcon, DeleteIcon } from '../../../_core/components/icons';
import { ArchiveIcon, EditIcon } from '../../../_core/constants/icons.constants';
import { EngagementsTableHandlers } from './engagements-table.component';
import { CoreIconButton } from '../../../_core/components/_ui/core-button';
import { ButtonBase, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { useCurrentAccessTokenSelector } from '../../../auth';

export type AllowedEngagementActions = 'view' | 'reminder';
export interface EngagementsTableActionsProps extends EngagementsTableHandlers {
	engagement: EngagementDto;
	allowedEngagementActions: AllowedEngagementActions[];
	isLinguistLink?: boolean;
	isAdminRoute?: boolean;
}

export const EngagementsLinguistLinkTableActions: FC<EngagementsTableActionsProps> = (props) => {
	const {
		engagement,
		allowedEngagementActions,
		isAdminRoute,
		onSendReminder,
		onView,
	} = props;
	const [t] = useTranslation();
	
	const handleViewEngagement = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			if (!engagement) {
				return;
			}

			onView?.(e, engagement);
		},
		[onView, engagement]
	);

	const handleSendReminderEngagement = useCallback(() => {
		if (!engagement) {
			return;
		}

		onSendReminder?.(engagement);
	}, [onSendReminder, engagement]);

	const currentToken = useCurrentAccessTokenSelector();
	const isSuperAdmin = currentToken?.payload.sur === UserRolesEnum.SuperAdmin;
	
	const actions = useMemo<CellAction[]>(
		() => [
			{
				id: 'view',
				type: 'button',
				tooltipTitle: t('buttons.tooltips.full-details'),
				hidden: !allowedEngagementActions.includes('view'),
				props: {
					children: (
						<span>
							<Link
								to={`/engagements/${engagement._id}/item/${engagement.projectItemId}?isLinguistLink=true${(isSuperAdmin && !isAdminRoute) ? `&districtId=${engagement.districtId}` : ''}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<EyeIcon sx={{ fontSize: 30 }} color="primary" />
							</Link>
						</span>
					),
					onClick: handleViewEngagement,
				},
			},
			{
				id: 'reminder',
				type: 'button',

				hidden: true,
				//tooltipTitle: engagement.canSendReminder ? t('buttons.tooltips.send') : undefined,
				props: {
					children: engagement.canSendReminder ? (
						<Tooltip title={t('buttons.tooltips.send')}>
							<span>
								<CoreIconButton onClick={handleSendReminderEngagement} disabled={!engagement.canSendReminder}>
									<ShareIcon
										sx={{
											fontSize: 30,
											color: engagement.canSendReminder ? 'primary.main' : 'rgba(60, 141, 188, 0.5)',
											cursor: engagement.canSendReminder ? 'pointer' : 'not-allowed',
										}}
									/>
								</CoreIconButton>
							</span>
						</Tooltip>
					) : (
						<Tooltip
							title={t('buttons.tooltips.no-reminder')}
							placement="top"
							arrow
							slotProps={{
								popper: {
									sx: {
										'& .MuiTooltip-tooltip': {
											backgroundColor: 'black',
											color: 'white',
										},
										'& .MuiTooltip-arrow': {
											color: 'black',
										},
									},
								},
							}}
						>
							<span>
								<CoreIconButton onClick={handleSendReminderEngagement} disabled={!engagement.canSendReminder}>
									<ShareIcon
										sx={{
											fontSize: 30,
											color: engagement.canSendReminder ? 'primary.main' : 'rgba(60, 141, 188, 0.5)',
											cursor: engagement.canSendReminder ? 'pointer' : 'not-allowed',
										}}
									/>
								</CoreIconButton>
							</span>
						</Tooltip>
					),
				},
			},
		],
		[allowedEngagementActions, handleViewEngagement, handleSendReminderEngagement]
	);

	return <CoreTableActionsCell actions={actions} />;
};
